import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {WritableDraft} from 'immer';
import {FetchStatus} from '../common/FetchStatus.enum';
import {SerializedPagewebError} from '../errors/SerializedPagewebError.interface';
import {thunkSerializeError} from '../errors/thunkSerializeError.function';
import {layoutFactory} from '../factory/layoutFactory';
import {
  AgeGrade,
  DynamicLayoutReference,
  HelixThemeSection,
  LayoutFragment,
  LayoutReferenceViewData,
  Maybe,
  MetaInfoTypeSection,
  SectionFragment,
  SectionType,
  StaticLandingDayOfWeekSection,
  StaticLandingEventSection,
  StaticLandingFreeSeriesSection,
  StaticLandingGenreSection,
  StaticLandingOriginalSection,
  StaticLandingRankingSection,
  StaticLandingRankingThemeSection,
  StaticLandingTodayUpSection,
  TabSection,
} from '../graphql/generated/types';
import {createQueryClient} from '../reactQuery/createQueryClient';
import {AppState} from '../state/store';
import {PAGEWEB_LAYOUT_STATE_NAME, SECTION_WITH_PAGING_LIST} from './constants';
import {initializeItemState} from './initializeItemState.function';
import {initializeLayoutState} from './initializeLayoutState.function';
import {initializeParentRelation} from './initializeParentRelation.function';
import {initializeSectionState} from './initializeSectionState.function';
import {PagewebLayoutState} from './PagewebLayoutState.interface';

export const INITIAL_STATE = {
  entities: {
    layouts: {},
    sections: {},
    groups: {},
    items: {},
  },
  fetchStatus: {},
  errors: {},
  relations: {
    layouts: {},
    sections: {},
    groups: {},
    nestedGroups: {},
    items: {},
  },
  extraInfos: {
    layouts: {},
    sections: {},
  },
  bannerInfo: {
    selectedBanner: {},
    subCardType: {},
    hasIndicator: {},
  },
  layoutIdAndScreenUidMap: {},
} as PagewebLayoutState;

export const fetchViewDataByGroupId = createAsyncThunk<
  LayoutReferenceViewData[] | null | undefined,
  {groupId: string},
  {state: AppState; serializedErrorType: SerializedPagewebError}
>(
  `${PAGEWEB_LAYOUT_STATE_NAME}/fetchViewDataByGroupId`,
  async ({groupId}, {getState}) => {
    const {entities, relations} = getState().pagewebLayout;
    const group = entities.groups[groupId];
    const section = entities.sections[relations.groups[groupId].parentId];

    const metaInfoType =
      'metaInfoType' in section
        ? (section as MetaInfoTypeSection).metaInfoType
        : undefined;
    if (!group.dataKey || !section) {
      throw new Error('dataKey가 없습니다');
    }
    const queryClient = createQueryClient();

    const data = await queryClient.fetchQuery(
      layoutFactory.layoutReference({
        dataKey: group.dataKey,
        sectionType: section.type,
        metaInfoType,
      })
    );

    return data;
  },
  {
    serializeError: thunkSerializeError,
  }
);

export const fetchDynamicLayoutByGroupId = createAsyncThunk<
  Maybe<DynamicLayoutReference> | undefined | null,
  {groupId: string},
  {state: AppState; serializedErrorType: SerializedPagewebError}
>(
  `${PAGEWEB_LAYOUT_STATE_NAME}/fetchDynamicLayoutByGroupId`,
  async ({groupId}, {getState}) => {
    const {entities, relations} = getState().pagewebLayout;
    const group = entities.groups[groupId];
    const section = entities.sections[relations.groups[groupId].parentId];

    const metaInfoType =
      'metaInfoType' in section
        ? (section as MetaInfoTypeSection).metaInfoType
        : undefined;
    if (!group.dataKey || !section) {
      throw new Error('dataKey가 없습니다');
    }
    const queryClient = createQueryClient();
    const data = await queryClient.fetchQuery(
      layoutFactory.dynamicLayoutReference({
        dataKey: group.dataKey,
        sectionType: section.type,
        metaInfoType,
      })
    );

    return data;
  },
  {
    serializeError: thunkSerializeError,
  }
);

export const fetchLayoutDataByScreenUid = createAsyncThunk<
  Maybe<LayoutFragment>,
  {screenUid: number},
  {state: AppState; serializedErrorType: SerializedPagewebError}
>(
  `${PAGEWEB_LAYOUT_STATE_NAME}/fetchLayoutDataByScreenUid`,
  async ({screenUid}) => {
    const queryClient = createQueryClient();
    const data = await queryClient.fetchQuery(
      layoutFactory.layout({
        screenUid,
      })
    );

    return data;
  },
  {
    serializeError: thunkSerializeError,
  }
);

export const pagewebLayoutSlice = createSlice({
  name: PAGEWEB_LAYOUT_STATE_NAME,
  initialState: INITIAL_STATE,
  reducers: {
    setLayoutIdAndScreenUidMap: (
      state,
      action: PayloadAction<{layoutId: string; screenUid: number}>
    ): void => {
      state.layoutIdAndScreenUidMap[action.payload.screenUid] =
        action.payload.layoutId;
    },
    setLayout: (
      state,
      action: PayloadAction<{
        layout: LayoutFragment;
        ageGrade?: Maybe<AgeGrade>;
      }>
    ): void => {
      const {layout, ageGrade} = action.payload;
      initializeLayoutState({
        state,
        id: layout.id,
        layout,
        screenUid: layout.screenUid,
        ageGrade,
      });
    },
    setSection: (
      state,
      action: PayloadAction<{section: SectionFragment; sectionId?: string}>
    ): void => {
      const {section, sectionId} = action.payload;
      if (!sectionId) {
        return;
      }
      const param = 'param' in section ? section.param : undefined;
      const page = param && 'page' in param ? param.page : undefined;

      // 페이징이 존재하는 페이지의 경우 기존 그룹에 다음 페이지 아이템을 추가하는 로직을 타야 합니다.
      if (SECTION_WITH_PAGING_LIST.includes(section.type) && (page || 0) > 0) {
        const {groups: _, ...entity} = section;
        state.entities.sections[sectionId] = entity;
        // 1 페이지 이상은 기존 그룹에 item 을 추가하기만 합니다.
        section.groups.forEach(group => {
          group.items?.forEach(item => {
            initializeItemState({
              id: item.id,
              parentId: group.id,
              item,
              state,
            });
          });
        });
      } else {
        // 다른 조건으로 검색한 section 이 들어오면 새롭게 섹션을 세팅합니다
        initializeParentRelation({
          state,
          relationKey: 'sections',
          id: sectionId,
          parentId: state.relations.sections[sectionId].parentId,
        });
        initializeSectionState({
          state,
          id: section.id,
          section,
          parentId: state.relations.sections[sectionId].parentId,
        });
      }
    },
    selectSectionMainTab: (
      state,
      action: PayloadAction<{
        sectionId: string;
        categoryTabUid: number;
      }>
    ): void => {
      const {sectionId, categoryTabUid} = action.payload;
      const section = state.entities.sections[sectionId] as TabSection;

      section.sectionMainTabList?.forEach(mainTab => {
        mainTab.isSelected = mainTab.uid === categoryTabUid;
      });
    },
    selectSectionSubTab: (
      state,
      action: PayloadAction<{
        sectionId: string;
        dayTabUid: number;
      }>
    ): void => {
      const {sectionId, dayTabUid} = action.payload;
      const section = state.entities.sections[sectionId] as TabSection;
      section.sectionMainTabList
        ?.find(mainTab => mainTab.isSelected)
        ?.subTabList.forEach(subTab => {
          subTab.isSelected = subTab.uid === dayTabUid;
        });
    },
    setSelectedBanner: (
      state,
      action: PayloadAction<{groupId: string; selectedItemId: string}>
    ): void => {
      const {groupId, selectedItemId} = action.payload;
      state.bannerInfo.selectedBanner[groupId] = selectedItemId;
    },
    setFetchStatus: (
      state,
      action: PayloadAction<{groupId: string; fetchStatus: FetchStatus}>
    ): void => {
      const {groupId, fetchStatus} = action.payload;
      state.fetchStatus[groupId] = fetchStatus;
    },
    setBannerHasIndicator: (
      state,
      action: PayloadAction<{groupId: string; hasIndicator: boolean}>
    ): void => {
      const {groupId, hasIndicator} = action.payload;
      state.bannerInfo.hasIndicator[groupId] = hasIndicator;
    },
    onSuccessStaticLandingSection: {
      reducer: (
        state,
        action: PayloadAction<
          StaticLandingSectionTypes | undefined,
          string,
          {sectionId: string; page: number}
        >
      ) => {
        const {sectionId, page = 0} = action.meta;
        setStaticLandingSectionFulfilled({state, action, sectionId, page});
      },
      prepare: (
        payload: StaticLandingSectionTypes | undefined,
        {sectionId, page = 0}: {sectionId: string; page: number}
      ) => ({payload, meta: {sectionId, page}}),
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchViewDataByGroupId.pending, (state, {meta: {arg}}) => {
      state.fetchStatus[arg.groupId] = FetchStatus.Loading;
    });
    builder.addCase(
      fetchViewDataByGroupId.fulfilled,
      (state, {meta: {arg}, payload}) => {
        if (!payload) {
          state.fetchStatus[arg.groupId] = FetchStatus.Error;
          return;
        }
        state.fetchStatus[arg.groupId] = FetchStatus.Success;
        const items = payload[0].items;

        const sectionId = state.relations.groups[arg.groupId].parentId;
        const section = state.entities.sections[sectionId];
        // 랭킹 테마 섹션은 노출 작품이 0개면 섹션 자체를 미노출 (KW-10484)
        if (
          section?.type === SectionType.RankingTheme &&
          (!items || items.length === 0)
        ) {
          const layoutId = state.relations.sections[sectionId].parentId;
          const sectionIds = state.relations.layouts[layoutId].childrenIds;
          const noItemsSectionIndex = sectionIds.findIndex(
            id => id === sectionId
          );
          if (noItemsSectionIndex !== -1) {
            state.relations.layouts[layoutId].childrenIds.splice(
              noItemsSectionIndex,
              1
            );
          }
          return;
        }

        state.entities.groups[arg.groupId].items = items;
        items?.forEach(item => {
          initializeItemState({
            state,
            parentId: arg.groupId,
            id: item.id,
            item,
          });
        });
      }
    );
    builder.addCase(
      fetchViewDataByGroupId.rejected,
      (state, {meta: {arg}, error}) => {
        state.fetchStatus[arg.groupId] = FetchStatus.Error;
        state.errors[arg.groupId] = new Error(error.message);
      }
    );

    builder.addCase(
      fetchDynamicLayoutByGroupId.pending,
      (state, {meta: {arg}}) => {
        state.fetchStatus[arg.groupId] = FetchStatus.Loading;
      }
    );
    builder.addCase(
      fetchDynamicLayoutByGroupId.fulfilled,
      (state, {meta: {arg}, payload}) => {
        const sectionId = state.relations.groups[arg.groupId].parentId;
        const items = payload?.layoutReference?.[0].items;
        // 엣지 케이스지만 items 가 없는 경우는 섹션을 삭제합니다.
        if (!items) {
          const layoutId = state.relations.sections[sectionId].parentId;
          const sectionIds = state.relations.layouts[layoutId].childrenIds;
          const noItemsSectionIndex = sectionIds.findIndex(
            id => id === sectionId
          );
          if (noItemsSectionIndex !== -1) {
            state.relations.layouts[layoutId].childrenIds.splice(
              noItemsSectionIndex,
              1
            );
          }
          return;
        }
        const helixThemeSection = state.entities.sections[sectionId] as Omit<
          HelixThemeSection,
          'groups'
        >;
        helixThemeSection.title = payload.dynamicLayout?.title;
        helixThemeSection.subtitle = payload.dynamicLayout?.subTitle;
        helixThemeSection.description = payload.dynamicLayout?.description;
        helixThemeSection.scheme = payload.dynamicLayout?.scheme;

        state.fetchStatus[arg.groupId] = FetchStatus.Success;
        state.entities.groups[arg.groupId].items =
          payload.layoutReference?.[0].items;
        payload.layoutReference?.[0].items?.forEach(item => {
          initializeItemState({
            state,
            parentId: arg.groupId,
            id: item.id,
            item,
          });
        });
      }
    );
    builder.addCase(
      fetchDynamicLayoutByGroupId.rejected,
      (state, {meta: {arg}, error}) => {
        state.fetchStatus[arg.groupId] = FetchStatus.Error;
        state.errors[arg.groupId] = new Error(error.message);
      }
    );

    builder.addCase(
      fetchLayoutDataByScreenUid.pending,
      (state, {meta: {arg}}) => {
        state.fetchStatus[arg.screenUid] = FetchStatus.Loading;
      }
    );
    builder.addCase(fetchLayoutDataByScreenUid.fulfilled, (state, action) => {
      const {screenUid} = action.meta.arg;

      if (!action.payload) {
        state.fetchStatus[screenUid] = FetchStatus.Error;
        return;
      }
      state.fetchStatus[screenUid] = FetchStatus.Success;
      pagewebLayoutSlice.caseReducers.setLayout(state, {
        ...action,
        payload: {
          layout: action.payload,
        },
      });
    });
    builder.addCase(
      fetchLayoutDataByScreenUid.rejected,
      (state, {meta: {arg}, error}) => {
        state.fetchStatus[arg.screenUid] = FetchStatus.Error;
        state.errors[arg.screenUid] = new Error(error.message);
      }
    );
  },
});

export const {
  setLayoutIdAndScreenUidMap,
  setLayout,
  selectSectionSubTab,
  selectSectionMainTab,
  setSelectedBanner,
  setBannerHasIndicator,
  setFetchStatus,
  onSuccessStaticLandingSection,
} = pagewebLayoutSlice.actions;

function setStaticLandingSectionFulfilled({
  state,
  action,
  sectionId,
  page = 0,
}: {
  state: WritableDraft<PagewebLayoutState>;
  action: PayloadAction<SectionFragment | undefined>;
  sectionId: string;
  page?: number;
}) {
  let groupId;
  // 오늘 UP 랜딩 페이지의 경우 첫 번째 자식 요소로 '내가 보던 작품' 그룹이 존재하므로
  // 두 번째 자식 요소를 선택합니다.
  if (state.relations.sections[sectionId].childrenIds.length > 1) {
    groupId = state.relations.sections[sectionId].childrenIds[1];
  } else {
    groupId = state.relations.sections[sectionId].childrenIds[0];
  }
  const isPageMoreQuery = page > 0;

  if (!action.payload) {
    state.fetchStatus[sectionId] = FetchStatus.Error;
    if (isPageMoreQuery) {
      state.fetchStatus[groupId] = FetchStatus.Error;
    }
    return;
  }

  state.fetchStatus[sectionId] = FetchStatus.Success;
  if (isPageMoreQuery) {
    state.fetchStatus[groupId] = FetchStatus.Success;
  }

  pagewebLayoutSlice.caseReducers.setSection(state, {
    ...action,
    payload: {
      sectionId,
      // @ts-ignore
      section: {
        ...action.payload,
        ...('totalCount' in state.entities.sections[sectionId] && {
          totalCount: (action.payload as {totalCount: Maybe<number>}).totalCount
            ? (action.payload as {totalCount: Maybe<number>}).totalCount
            : // @ts-ignore 위에서 이미 totalCount in 으로 체크함
              state.entities.sections[sectionId].totalCount,
        }),
        /**
         * 랭킹 페이지네이션의 마지막 요청(is_end=true)에서 rankingTime이 null로 내려오는 케이스가 있습니다.
         * 이는 API의 의도된 동작이며, 화면에 빈 값이 노출되지 않도록 이전 요청의 rankingTime을 유지합니다.
         */
        ...('rankingTime' in state.entities.sections[sectionId] && {
          rankingTime: (action.payload as {rankingTime: Maybe<string>})
            .rankingTime
            ? (action.payload as {rankingTime: Maybe<string>}).rankingTime
            : state.entities.sections[sectionId].rankingTime,
        }),
        /**
         * RankingTheme 서브탭: page>0 응답에 메타 필드(필터/정렬/배너)가 빠져 있는 경우
         * 이전 페이지의 값을 유지하여 UI 가 깜빡이거나 사라지지 않도록 한다.
         */
        ...('filterOptList' in state.entities.sections[sectionId] && {
          filterOptList: (action.payload as {filterOptList?: unknown[]})
            .filterOptList?.length
            ? (action.payload as {filterOptList?: unknown[]}).filterOptList
            : // @ts-ignore
              state.entities.sections[sectionId].filterOptList,
        }),
        ...('rankSortOptList' in state.entities.sections[sectionId] && {
          rankSortOptList: (action.payload as {rankSortOptList?: unknown[]})
            .rankSortOptList?.length
            ? (action.payload as {rankSortOptList?: unknown[]}).rankSortOptList
            : // @ts-ignore
              state.entities.sections[sectionId].rankSortOptList,
        }),
        ...('selectedFilterOpt' in state.entities.sections[sectionId] && {
          selectedFilterOpt: (action.payload as {selectedFilterOpt?: unknown})
            .selectedFilterOpt
            ? (action.payload as {selectedFilterOpt?: unknown})
                .selectedFilterOpt
            : // @ts-ignore
              state.entities.sections[sectionId].selectedFilterOpt,
        }),
        ...('selectedRankSortOpt' in state.entities.sections[sectionId] && {
          selectedRankSortOpt: (
            action.payload as {selectedRankSortOpt?: unknown}
          ).selectedRankSortOpt
            ? (action.payload as {selectedRankSortOpt?: unknown})
                .selectedRankSortOpt
            : // @ts-ignore
              state.entities.sections[sectionId].selectedRankSortOpt,
        }),
        ...('promotionBanner' in state.entities.sections[sectionId] && {
          promotionBanner: (action.payload as {promotionBanner?: unknown})
            .promotionBanner
            ? (action.payload as {promotionBanner?: unknown}).promotionBanner
            : // @ts-ignore
              state.entities.sections[sectionId].promotionBanner,
        }),
      },
    },
  });
}

type StaticLandingSectionTypes =
  | StaticLandingRankingSection
  | StaticLandingRankingThemeSection
  | StaticLandingDayOfWeekSection
  | StaticLandingFreeSeriesSection
  | StaticLandingGenreSection
  | StaticLandingTodayUpSection
  | StaticLandingOriginalSection
  | StaticLandingEventSection;
